<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import $api from '@/utils/api.js'
import { VDataTable } from 'vuetify/labs/VDataTable'

const { t } = useI18n()

definePage({
    meta: {
        action: 'view',
        subject: 'translation',
    },
})

// Set page title
useHead({
    title: t('menu.translations'),
    meta: [
        { name: 'description', content: t('admin_notifications.translations_description') }
    ]
})

// Reactive data
const loading = ref(false)
const locales = ref([])
const allTranslations = ref({})
const selectedLocale = ref('en')
const searchQuery = ref('')
const editDialog = ref(false)
const editingTranslation = ref({
    locale: '',
    group: '',
    key: '',
    value: ''
})

// DataTable settings
const itemsPerPage = ref(25)
const currentPage = ref(1)
const sortBy = ref([{ key: 'key', order: 'asc' }])
const headers = ref([
    { title: 'Key', key: 'key', sortable: true },
    { title: 'Value', key: 'value', sortable: true },
    { title: 'Group', key: 'group', sortable: true },
    { title: 'Actions', key: 'actions', sortable: false, width: '100px' }
])

// Computed
const flattenedTranslations = computed(() => {
    const flatten = (obj, prefix = '') => {
        const result = {}
        for (const key in obj) {
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                Object.assign(result, flatten(obj[key], prefix + key + '.'))
            } else {
                result[prefix + key] = obj[key]
            }
        }
        return result
    }
    return flatten(allTranslations.value)
})

const filteredTranslations = computed(() => {
    if (!searchQuery.value) return flattenedTranslations.value

    const filtered = {}
    const flat = flattenedTranslations.value
    Object.keys(flat).forEach(key => {
        if (key.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            flat[key].toLowerCase().includes(searchQuery.value.toLowerCase())) {
            filtered[key] = flat[key]
        }
    })
    return filtered
})

const tableItems = computed(() => {
    const flat = filteredTranslations.value
    return Object.keys(flat).map(key => ({
        key: key,
        value: flat[key],
        group: key.split('.')[0],
        fullKey: key
    }))
})

const translationCount = computed(() => Object.keys(flattenedTranslations.value).length)

// Methods
const loadLocales = async () => {
    try {
        const response = await $api('/translations')
        if (response.data.success) {
            locales.value = response.data.data
            if (response.data.data.length > 0 && !selectedLocale.value) {
                selectedLocale.value = response.data.data[0]
            }
        }
    } catch (error) {
        console.error('Error loading locales:', error)
    }
}

const loadAllTranslations = async () => {
    if (!selectedLocale.value) return

    loading.value = true
    try {
        const response = await $api(`/admin/translations/${selectedLocale.value}/all`)
        if (response.data.success) {
            allTranslations.value = response.data.data
        }
    } catch (error) {
        console.error('Error loading translations:', error)
    } finally {
        loading.value = false
    }
}

const openEditDialog = (item) => {
    editingTranslation.value = {
        locale: selectedLocale.value,
        group: item.group,
        key: item.fullKey,
        value: item.value
    }
    editDialog.value = true
}

const saveTranslation = async () => {
    loading.value = true
    try {
        const response = await $api('/admin/translations/update', {
            method: 'POST',
            data: editingTranslation.value
        })

        if (response.data.success) {
            // Update local data in nested structure
            setNestedValue(allTranslations.value, editingTranslation.value.key, editingTranslation.value.value)
            editDialog.value = false

            // Show success message
        }
    } catch (error) {
        console.error('Error saving translation:', error)
    } finally {
        loading.value = false
    }
}

// Helper function to set nested value
const setNestedValue = (obj, key, value) => {
    const keys = key.split('.')
    let current = obj

    for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
            current[keys[i]] = {}
        }
        current = current[keys[i]]
    }

    current[keys[keys.length - 1]] = value
}

// Helper function to get icon based on key
const getKeyIcon = (key) => {
    if (key.startsWith('$vuetify.')) return 'tabler-components'
    if (key.startsWith('user.')) return 'tabler-user'
    if (key.startsWith('admin_notifications.')) return 'tabler-bell'
    if (key.startsWith('menu.')) return 'tabler-menu-2'
    if (key.startsWith('job.')) return 'tabler-file-text'
    if (key.startsWith('logs.')) return 'tabler-file-text'
    return 'tabler-language'
}

// Helper function to get CSS class based on key depth
const getKeyClass = (key) => {
    const depth = key.split('.').length
    if (depth === 1) return 'text-primary font-weight-bold'
    if (depth === 2) return 'text-secondary'
    return 'text-body-2'
}

const importFromFiles = async () => {
    loading.value = true
    try {
        const response = await $api('/admin/translations/import', {
            method: 'POST'
        })

        if (response.success) {
            await loadAllTranslations()
        }
    } catch (error) {
        console.error('Error importing translations:', error)
    } finally {
        loading.value = false
    }
}

const closeEditDialog = () => {
    editDialog.value = false
    editingTranslation.value = {
        locale: '',
        group: '',
        key: '',
        value: ''
    }
}

// Watchers
watch(selectedLocale, async () => {
    allTranslations.value = {}
    await loadAllTranslations()
})

// Lifecycle
onMounted(async () => {
    await loadLocales()
    await loadAllTranslations()
})
</script>

<template>
    <div>
        <!-- Header -->
        <div class="d-flex justify-space-between align-center mb-6">
            <div>
                <h4 class="text-h4 mb-1">
                    Translation Management
                </h4>
                <p class="text-body-1 mb-0">
                    Manage application translations
                </p>
            </div>

            <VBtn color="primary" @click="importFromFiles" :loading="loading" prepend-icon="tabler-download">
                Import from Files
            </VBtn>
        </div>

        <!-- Filters -->
        <VCard class="mb-6">
            <VCardText>
                <VRow>
                    <VCol cols="12" md="4">
                        <VSelect v-model="selectedLocale" :items="locales" label="Locale" variant="outlined"
                            density="compact" />
                    </VCol>

                    <VCol cols="12" md="8">
                        <VTextField v-model="searchQuery" label="Search translations" variant="outlined"
                            density="compact" prepend-inner-icon="tabler-search" clearable />
                    </VCol>
                </VRow>
            </VCardText>
        </VCard>

        <!-- Translations Table -->
        <VCard>
            <!-- Table Header -->
            <VCardText
                class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between pa-4 gap-3">
                <div class="d-flex align-center gap-2">
                    <VIcon icon="tabler-language" size="34" />
                    <h2 class="text-h3 font-weight-bold">Translations</h2>
                </div>

                <div class="d-flex align-center gap-2 flex-wrap">
                    <VChip v-if="translationCount" color="primary" size="small">
                        {{ translationCount }} items
                    </VChip>
                    <VBtn color="primary" @click="importFromFiles" :loading="loading" prepend-icon="tabler-download">
                        Import from Files
                    </VBtn>
                </div>
            </VCardText>

            <VDivider />

            <VCardText v-if="loading" class="text-center py-8">
                <VProgressCircular indeterminate color="primary" size="64" />
                <p class="mt-4 text-body-1">Loading translations...</p>
            </VCardText>

            <VCardText v-else-if="!selectedLocale" class="text-center py-8">
                <VIcon icon="tabler-language" size="64" color="grey-lighten-1" />
                <p class="mt-4 text-body-1">Select a locale to view translations</p>
            </VCardText>

            <VDataTable v-else :headers="headers" :items="tableItems" :items-per-page="itemsPerPage" density="compact"
                :search="searchQuery" :sort-by="sortBy" class="translation-table" item-key="fullKey" :loading="loading"
                loading-text="Loading translations..." no-data-text="No translations found"
                no-results-text="No translations match your search">
                <!-- Key column -->
                <template #item.key="{ item }">
                    <div class="d-flex align-center">
                        <VIcon :icon="getKeyIcon(item.fullKey)" color="primary" class="me-2" size="20" />
                        <span :class="getKeyClass(item.fullKey)">{{ item.key }}</span>
                    </div>
                </template>

                <!-- Value column -->
                <template #item.value="{ item }">
                    <div class="text-wrap" style="max-width: 300px;">
                        {{ item.value }}
                    </div>
                </template>

                <!-- Group column -->
                <template #item.group="{ item }">
                    <VChip size="small" color="primary" variant="tonal">
                        {{ item.group }}
                    </VChip>
                </template>

                <!-- Actions column -->
                <template #item.actions="{ item }">
                    <VBtn icon="tabler-edit" size="small" variant="text" color="primary"
                        @click="openEditDialog(item)" />
                </template>


            </VDataTable>
        </VCard>

        <!-- Edit Dialog -->
        <VDialog v-model="editDialog" max-width="600">
            <VCard>
                <VCardTitle>
                    Edit Translation
                </VCardTitle>

                <VCardText>
                    <VRow>
                        <VCol cols="12" md="6">
                            <VTextField v-model="editingTranslation.locale" label="Locale" variant="outlined"
                                readonly />
                        </VCol>

                        <VCol cols="12" md="6">
                            <VTextField v-model="editingTranslation.group" label="Group" variant="outlined" readonly />
                        </VCol>

                        <VCol cols="12">
                            <VTextField v-model="editingTranslation.key" label="Key" variant="outlined" readonly />
                        </VCol>

                        <VCol cols="12">
                            <VTextarea v-model="editingTranslation.value" label="Translation Value" variant="outlined"
                                rows="3" auto-grow />
                        </VCol>
                    </VRow>
                </VCardText>

                <VCardActions>
                    <VSpacer />

                    <VBtn variant="text" @click="closeEditDialog">
                        Cancel
                    </VBtn>

                    <VBtn color="primary" @click="saveTranslation" :loading="loading">
                        Save
                    </VBtn>
                </VCardActions>
            </VCard>
        </VDialog>
    </div>
</template>

<style scoped>
.translation-table {
    border-radius: 8px;
}

.translation-table :deep(.v-data-table__wrapper) {
    border-radius: 8px;
}

.translation-table :deep(.v-data-table-header) {
    background-color: rgba(var(--v-theme-primary), 0.04);
}

.translation-table :deep(.v-data-table__td) {
    padding: 12px 16px;
}

.translation-table :deep(.v-data-table__th) {
    padding: 12px 16px;
    font-weight: 600;
}

.text-wrap {
    word-wrap: break-word;
    white-space: pre-wrap;
}
</style>